/**
 * Stable keys for SubAccountant per-client menu restrictions.
 * The database (SubAccountantRestrictionKeyDefinition) is the source of truth for which keys can be assigned;
 * this file defines keys used for route protection and menu filtering.
 *
 * --- Adding new keys ---
 * 1. Add the key here: MENU_CATEGORIES (if category) or MENU_ITEMS (if item).
 * 2. If item: add mapping in ITEM_TO_CATEGORY (itemKey -> categoryKey).
 * 3. Add default label in menuRestrictionTranslationKeys.js (MENU_CATEGORY_LABELS or MENU_ITEM_LABELS).
 * 4. Sync to DB so the key can be assigned to sub-accountants:
 *    - From code: buildRestrictionKeysSyncPayload() from Utils/restrictionKeysSync.js, then
 *      PermissionService.SyncRestrictionKeyDefinitions(payload, token).
 *    - Or add a single key: PermissionService.AddRestrictionKeyDefinition({ key, categoryKey?, displayName, sortOrder }, token).
 * 5. Optionally add translation keys in translationEN/HE/AR for the restrictions UI.
 */

export const MENU_CATEGORIES = {
  Accounting: 'Accounting',
  Reports: 'Reports',
  Sales: 'Sales',
  Bank: 'Bank',
  Documents: 'Documents',
  Vehicles: 'Vehicles',
  Garage: 'Garage',
  Audit: 'Audit',
  Settings: 'Settings',
};

export const MENU_ITEMS = {
  AccountCardsTable: 'AccountCardsTable',
  AccountCardDashboard: 'AccountCardDashboard',
  Incomes: 'Incomes',
  Expenses: 'Expenses',
  PaymentAcceptances: 'PaymentAcceptances',
  PrintChecks: 'PrintChecks',
  JournalEntries: 'JournalEntries',
  Projects: 'Projects',
  Assets: 'Assets',
  HoldingItems: 'HoldingItems',
  DocumentsRequiringClarification: 'DocumentsRequiringClarification',
  SortCodeReport: 'SortCodeReport',
  ClientExpenseAssignmentReport: 'ClientExpenseAssignmentReport',
  TrialBalance: 'TrialBalance',
  ReportDetailedProfitAndLoss: 'ReportDetailedProfitAndLoss',
  ProfittAndLoss6111: 'ProfittAndLoss6111',
  AnnualReportQuestionnaire: 'AnnualReportQuestionnaire',
  DebtorsCreditorsLedgerReport: 'DebtorsCreditorsLedgerReport',
  ReceivablesAging: 'ReceivablesAging',
  CashFlowForecast: 'CashFlowForecast',
  ForeignTravelReport: 'ForeignTravelReport',
  DownloadReceptionLog: 'DownloadReceptionLog',
  AdvancesReport: 'AdvancesReport',
  PCNFile: 'PCNFile',
  VATReport: 'VATReport',
  WithholdingTaxReports: 'WithholdingTaxReports',
  ClientWithholdingTaxReports: 'ClientWithholdingTaxReports',
  Item856: '856',
  UniformFile: 'UniformFile',
  ReferenceSequence: 'ReferenceSequence',
  DocumentItems: 'DocumentItems',
  InventoryManagement: 'InventoryManagement',
  DeliveryNote: 'DeliveryNote',
  ReturnNote: 'ReturnNote',
  TransactionDocuments: 'TransactionDocuments',
  CancelDocuments: 'CancelDocuments',
  TaxInvoice: 'TaxInvoice',
  ReceiptTax: 'ReceiptTax',
  LawyerInvoice: 'LawyerInvoice',
  LawyerReceipt: 'LawyerReceipt',
  FixedPurchaseSale: 'FixedPurchaseSale',
  FixedPurchaseSaleReceipt: 'FixedPurchaseSaleReceipt',
  CreditNote: 'CreditNote',
  Receipt: 'Receipt',
  Transaction: 'Transaction',
  Quotation: 'Quotation',
  Order: 'Order',
  SelfInvoice: 'SelfInvoice',
  VehicleManagement: 'VehicleManagement',
  GarageWorkDiary: 'GarageWorkDiary',
  Audit: 'Audit',
  BankMatching: 'BankMatching',
  CardMatching: 'CardMatching',
  BankPages: 'BankPages',
  OtherDocuments: 'OtherDocuments',
  PermanentFile: 'PermanentFile',
  SignatureDocuments: 'SignatureDocuments',
  Forms: 'Forms',
  ProfittAndLoss6111: 'ProfittAndLoss6111',
  Report1214: 'Report1214',
  Report1214B: 'Report1214B',
  Report1281: 'Report1281',
  Report1362: 'Report1362',
  EditClient: 'EditClient',
};

/** Map menu item key -> category key for "deny category" logic */
export const ITEM_TO_CATEGORY = {
  [MENU_ITEMS.AccountCardsTable]: MENU_CATEGORIES.Settings,
  [MENU_ITEMS.AccountCardDashboard]: MENU_CATEGORIES.Accounting,
  [MENU_ITEMS.Incomes]: MENU_CATEGORIES.Accounting,
  [MENU_ITEMS.Expenses]: MENU_CATEGORIES.Accounting,
  [MENU_ITEMS.PaymentAcceptances]: MENU_CATEGORIES.Accounting,
  [MENU_ITEMS.PrintChecks]: MENU_CATEGORIES.Accounting,
  [MENU_ITEMS.JournalEntries]: MENU_CATEGORIES.Accounting,
  [MENU_ITEMS.Projects]: MENU_CATEGORIES.Settings,
  [MENU_ITEMS.Assets]: MENU_CATEGORIES.Accounting,
  [MENU_ITEMS.HoldingItems]: MENU_CATEGORIES.Accounting,
  [MENU_ITEMS.DocumentsRequiringClarification]: MENU_CATEGORIES.Accounting,
  [MENU_ITEMS.SortCodeReport]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.ClientExpenseAssignmentReport]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.TrialBalance]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.ReportDetailedProfitAndLoss]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.ProfittAndLoss6111]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.Report1214]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.Report1214B]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.Report1281]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.Report1362]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.AnnualReportQuestionnaire]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.DebtorsCreditorsLedgerReport]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.ReceivablesAging]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.CashFlowForecast]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.ForeignTravelReport]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.DownloadReceptionLog]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.ProfittAndLoss6111]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.AdvancesReport]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.PCNFile]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.VATReport]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.WithholdingTaxReports]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.ClientWithholdingTaxReports]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.Item856]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.UniformFile]: MENU_CATEGORIES.Reports,
  [MENU_ITEMS.ReferenceSequence]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.DocumentItems]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.InventoryManagement]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.DeliveryNote]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.ReturnNote]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.TransactionDocuments]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.CancelDocuments]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.TaxInvoice]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.ReceiptTax]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.LawyerInvoice]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.LawyerReceipt]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.FixedPurchaseSale]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.FixedPurchaseSaleReceipt]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.CreditNote]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.Receipt]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.Transaction]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.Quotation]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.Order]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.SelfInvoice]: MENU_CATEGORIES.Sales,
  [MENU_ITEMS.VehicleManagement]: MENU_CATEGORIES.Vehicles,
  [MENU_ITEMS.GarageWorkDiary]: MENU_CATEGORIES.Garage,
  [MENU_ITEMS.Audit]: MENU_CATEGORIES.Audit,
  [MENU_ITEMS.BankMatching]: MENU_CATEGORIES.Bank,
  [MENU_ITEMS.CardMatching]: MENU_CATEGORIES.Bank,
  [MENU_ITEMS.BankPages]: MENU_CATEGORIES.Bank,
  [MENU_ITEMS.OtherDocuments]: MENU_CATEGORIES.Documents,
  [MENU_ITEMS.PermanentFile]: MENU_CATEGORIES.Documents,
  [MENU_ITEMS.SignatureDocuments]: MENU_CATEGORIES.Documents,
  [MENU_ITEMS.Forms]: MENU_CATEGORIES.Documents,
  [MENU_ITEMS.EditClient]: MENU_CATEGORIES.Settings,
};

/**
 * Returns true if the menu item or its category is in the restrictions list.
 */
export function isRestricted(restrictionKeys, itemKey) {
  if (!restrictionKeys || restrictionKeys.length === 0) return false;
  const set = new Set(restrictionKeys);
  if (set.has(itemKey)) return true;
  const category = ITEM_TO_CATEGORY[itemKey];
  return category ? set.has(category) : false;
}

/**
 * Returns true if the category is in the restrictions list.
 */
export function isCategoryRestricted(restrictionKeys, categoryKey) {
  if (!restrictionKeys || restrictionKeys.length === 0) return false;
  return new Set(restrictionKeys).has(categoryKey);
}
